/**
 * "Authenticating your users" — Auth section (ADR-0074).
 * Reconciled from product/architecture/auth.md (the four auth modes) and the
 * widget auth wiring. The practical "which mode do I pick" guide; the concepts
 * live in the Core-concepts `identity` page.
 */
import type { DocPageContent } from '../types';

const SIGN_TOKEN = `import { SignJWT } from 'jose';

const secret = new TextEncoder().encode(process.env.SYNCANIX_SESSION_SECRET);

app.get('/api/chat-token', requireLogin, async (req, res) => {
  const token = await new SignJWT({ tenant: process.env.SYNCANIX_WORKSPACE_ID })
    .setProtectedHeader({ alg: 'HS256' })
    .setSubject(req.user.id)
    .setAudience('syncanix-tools')
    .setIssuedAt()
    .setExpirationTime('10m')
    .sign(secret);
  res.json({ token });
});`;

const WIRE_WIDGET = `window.syncanix?.setTokenProvider(async () => {
  const res = await fetch('/api/chat-token');
  const { token } = await res.json();
  return token;
});`;

export const AUTH_END_USER_PAGE: DocPageContent = {
  slug: 'auth/end-user-auth',
  titleKey: 'docs.auth.endUser.title',
  descriptionKey: 'docs.auth.endUser.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.auth.endUser.lead' },

    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.auth.endUser.rule.title',
      textKey: 'docs.auth.endUser.rule.body',
    },

    { kind: 'heading', level: 2, id: 'modes', textKey: 'docs.auth.endUser.modes.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.modes.body' },

    {
      kind: 'heading',
      level: 3,
      id: 'signed-session',
      textKey: 'docs.auth.endUser.signed.heading',
    },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.signed.body' },

    { kind: 'heading', level: 3, id: 'idp-adapter', textKey: 'docs.auth.endUser.idp.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.idp.body' },

    {
      kind: 'heading',
      level: 3,
      id: 'token-exchange',
      textKey: 'docs.auth.endUser.exchange.heading',
    },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.exchange.body' },

    { kind: 'heading', level: 3, id: 'bff', textKey: 'docs.auth.endUser.bff.heading' },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.bff.body' },

    {
      kind: 'heading',
      level: 2,
      id: 'signed-walkthrough',
      textKey: 'docs.auth.endUser.walkthrough.heading',
    },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.walkthrough.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.auth.endUser.walkthrough.step.secret.title',
          textKey: 'docs.auth.endUser.walkthrough.step.secret.text',
        },
        {
          titleKey: 'docs.auth.endUser.walkthrough.step.endpoint.title',
          textKey: 'docs.auth.endUser.walkthrough.step.endpoint.text',
        },
        {
          titleKey: 'docs.auth.endUser.walkthrough.step.wire.title',
          textKey: 'docs.auth.endUser.walkthrough.step.wire.text',
        },
      ],
    },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.walkthrough.serverIntro' },
    { kind: 'code', language: 'js', code: SIGN_TOKEN },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.walkthrough.claimsIntro' },
    {
      kind: 'list',
      itemKeys: [
        'docs.auth.endUser.walkthrough.claim.aud',
        'docs.auth.endUser.walkthrough.claim.sub',
        'docs.auth.endUser.walkthrough.claim.tenant',
        'docs.auth.endUser.walkthrough.claim.exp',
      ],
    },
    { kind: 'paragraph', textKey: 'docs.auth.endUser.walkthrough.widgetIntro' },
    { kind: 'code', language: 'js', code: WIRE_WIDGET },
    {
      kind: 'callout',
      tone: 'caution',
      titleKey: 'docs.auth.endUser.walkthrough.caution.title',
      textKey: 'docs.auth.endUser.walkthrough.caution.body',
    },

    { kind: 'heading', level: 2, id: 'choosing', textKey: 'docs.auth.endUser.choosing.heading' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.auth.endUser.choose.fast.term',
          descKey: 'docs.auth.endUser.choose.fast.desc',
        },
        {
          termKey: 'docs.auth.endUser.choose.idp.term',
          descKey: 'docs.auth.endUser.choose.idp.desc',
        },
        {
          termKey: 'docs.auth.endUser.choose.enterprise.term',
          descKey: 'docs.auth.endUser.choose.enterprise.desc',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.auth.endUser.rollout.title',
      textKey: 'docs.auth.endUser.rollout.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.auth.endUser.next.providers.title',
          textKey: 'docs.auth.endUser.next.providers.text',
          to: { docSlug: 'auth/identity-providers' },
        },
        {
          titleKey: 'docs.auth.endUser.next.stepup.title',
          textKey: 'docs.auth.endUser.next.stepup.text',
          to: { docSlug: 'auth/step-up' },
        },
      ],
    },
  ],
};
